/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, keyframes } from '@emotion/core';
import { List, message, Modal, Progress, Typography } from 'antd';
import { uniqBy } from 'lodash';
import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { queryCache } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  FileProgressItem,
  FileProgressItemProps,
} from '~components/atoms/FileProgressItem/FileProgressItem';
import IconButton from '~components/_common/IconButton/IconButton';
import { useCurrentFolder } from '~src/hooks/useCurrentFolder';
import { useProjectId } from '~src/hooks/useProjectId';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import {
  deleteCommit,
  deleteFolder,
  idOrNull,
  updateCommit,
} from '~src/services/api';
import { SimpleObject } from '~src/types';
import { DbCommit, DbFile, DbFolder } from '~src/types/db';
import { DB_STATE } from '~src/types/enums';
import FileProgressContext, {
  AttachmentProgress,
  FileProgress,
} from '~src/contexts/FileProgressContext';
import { appLog } from '~src/utils/log';
import { parseMessagePreview } from '~src/utils/messagePreview';
import UploadContext from '~src/contexts/UploadContext';
import { DETAILED_UPLOAD_LIMIT } from '~src/config';
import { useStore } from '~src/store';

interface FileProgressPanelProps {
  mobile?: boolean;
}

/** FileProgressPanel */
export const FileProgressPanel: React.FC<FileProgressPanelProps> = ({
  mobile,
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();
  const projectId = useProjectId();
  const currentFolder = useCurrentFolder();
  const { fileProgressContext, setFileProgressContext } = useContext(
    FileProgressContext
  );
  const { uploadContext, setUploadContext } = useContext(UploadContext);
  const { isUploadingActive, changeUploadingActive } = useStore();

  const [expanded, setExpanded] = useState(true);
  const filesDoneRef = useRef<SimpleObject<string[]>>({});
  const [modalData, setModalData] = useState<ModalDataState>({
    visible: false,
    commits: [],
    isLoading: false,
  });

  const [totalUploads, setTotalUploads] = useState<number>(
    fileProgressContext.length
  );
  const [completedUploads, setCompletedUploads] = useState<number>(0);

  const hasItemsInProgress = fileProgressContext.some(
    (v) => !v.isDone && !v.error
  );

  const hasItemsWithError = fileProgressContext.some((v) => v.error);

  useEffect(() => {
    if (!hasItemsInProgress) return;

    const beforeUnloadListener = (event: BeforeUnloadEvent) => {
      event.preventDefault();

      event.returnValue = intl.formatMessage({
        id: 'prevent-navigation-upload-message',
      });

      return event.returnValue;
    };

    window.addEventListener('beforeunload', beforeUnloadListener, {
      capture: true,
    });

    return () => {
      window.removeEventListener('beforeunload', beforeUnloadListener, {
        capture: true,
      });
    };
  }, [hasItemsInProgress, intl]);

  useEffect(() => {
    // when adding uploads, expand
    if (fileProgressContext.length && !expanded) {
      setExpanded(true);
    }
  }, [fileProgressContext.length]);

  useEffect(() => {
    setTotalUploads(fileProgressContext.length);
  }, [fileProgressContext.length]);

  useEffect(() => {
    setCompletedUploads(
      fileProgressContext.filter((item) => item.isDone).length
    );
  }, [fileProgressContext]);

  useEffect(() => {
    if (!hasItemsInProgress && hasItemsWithError) {
      const numberOfErrors = fileProgressContext.filter((item) => item.error)
        .length;
      message.error(
        intl.formatMessage(
          { id: 'upload-error-items' },
          {
            numberOfFiles: numberOfErrors,
          }
        )
      );
    }
  }, [hasItemsInProgress]);

  useEffect(() => {
    if (!fileProgressContext.length) return;

    const hasActiveUpload = fileProgressContext.some(
      (item) => item.shouldStartUpload && !item.isDone && !item.error
    );

    if (hasActiveUpload) return;

    const hasQueuedUpload = fileProgressContext.some(
      (item) => !item.shouldStartUpload && !item.isDone && !item.error
    );

    if (!hasQueuedUpload) return;

    setFileProgressContext((prevState) => {
      const nextIndex = prevState.findIndex(
        (item) => !item.shouldStartUpload && !item.isDone && !item.error
      );

      if (nextIndex < 0) return prevState;

      const newList = [...prevState];

      newList[nextIndex] = {
        ...prevState[nextIndex],
        shouldStartUpload: true,
      };

      return newList;
    });
  }, [fileProgressContext, setFileProgressContext]);

  // Manage upload blocking state for bulk uploads
  useEffect(() => {
    const isBulkMode = fileProgressContext.length > DETAILED_UPLOAD_LIMIT;

    if (isBulkMode && hasItemsInProgress) {
      changeUploadingActive(true);
    } else if (!hasItemsInProgress) {
      changeUploadingActive(false);
    }
  }, [hasItemsInProgress, changeUploadingActive, fileProgressContext.length]);

  const handleExpandClick = () => setExpanded((prevExpanded) => !prevExpanded);

  const handleFileDone: FileProgressItemProps['onFileDone'] = async (
    { id, commit, projectId },
    { isFileDelete } = { isFileDelete: false }
  ) => {
    if (!filesDoneRef.current[commit?.id])
      filesDoneRef.current[commit?.id] = [];

    if (!isFileDelete) {
      filesDoneRef.current[commit?.id].push(id);
    }

    const doneIds = filesDoneRef.current[commit?.id];
    const allIds = fileProgressContext
      .filter((v) => {
        const matchesCommit = v?.commit?.id === commit?.id;
        if (!isFileDelete) return matchesCommit;

        // if isFileDelete, leave out item
        return matchesCommit && v.id !== id;
      })
      .map((v) => v.id);

    // commit not ready
    if (allIds.length !== doneIds.length) return;
    // there was a mistake
    if (doneIds.some((doneId) => !allIds.includes(doneId))) {
      appLog(
        'FileProgressPanel -> filesDoneRef mismatch commitId:',
        commit?.id,
        filesDoneRef.current[commit?.id]
      );
    }

    const commitData = { ...commit, state: DB_STATE.PROCESSED };
    try {
      // update commit to "success", then invalidate filesQuery
      await updateCommit({
        projectId: String(projectId),
        commitData,
      });

      queryCache.invalidateQueries('folders');

      const baseKey = {
        projectId: String(projectId),
        folderId: idOrNull(currentFolder?.id),
      };

      // for files queries, invalidate current folder, and remove the rest so they load again on navigation
      queryCache.invalidateQueries('files');

      const filesQueries = queryCache.getQueries<DbFile[]>('files');
      filesQueries.forEach((query) => {
        const folderId = query.queryKey[1] && query.queryKey[1]['folderId'];
        // if not current folder match, remove
        if (folderId !== baseKey['folderId']) {
          queryCache.removeQuery(query);
        }
      });

      // remove data for query used in FileProgressItem, so it loads fresh data on next upload
      queryCache.removeQueries([
        'files',
        {
          ...baseKey,
          withVersions: false,
        },
      ]);

      if (commit.description.length > 1) {
        message.success(
          intl.formatMessage(
            { id: 'commit-created' },
            { message: parseMessagePreview(commit.description, 30) }
          )
        );
      }

      if (totalUploads <= DETAILED_UPLOAD_LIMIT) {
        setTimeout(() => setFileProgressContext([]), 5000);
      }
    } catch (err) {
      message.error(intl.formatMessage({ id: 'commit-create-failed' }));
      appLog('FileProgressPanel -> updateCommit -> commit', commitData);
      appLog('FileProgressPanel -> updateCommit -> err', err);
    }
  };

  const handleAttachmentDone: FileProgressItemProps['onFileDone'] = async (
    file,
    { isFileDelete } = { isFileDelete: false }
  ) => {
    queryCache.invalidateQueries('tendersTenderAttachments');

    if (totalUploads <= DETAILED_UPLOAD_LIMIT) {
      setTimeout(() => setFileProgressContext([]), 5000);
    }
    setUploadContext([]);
  };

  const handleCloseClick = () => {
    if (!hasItemsWithError) {
      changeUploadingActive(false);
      setFileProgressContext([]);
      return;
    }

    const commitsWithError = uniqBy(
      fileProgressContext.filter((v) => v.error).map((v) => v?.commit),
      'id'
    );
    setModalData((prevData) => ({
      ...prevData,
      commits: commitsWithError,
      visible: true,
    }));
  };

  const handleDeleteCommitsClick = async () => {
    const { commits } = modalData;
    if (!commits.length) {
      setModalData((prevData) => ({ ...prevData, visible: false }));
      return;
    }

    setModalData((prevData) => ({ ...prevData, isLoading: true }));

    try {
      // keep a list of new folders part of failed commits to be deleted
      // const foldersToDelete = uniqBy(
      //   commits
      //     .map(
      //       (c) =>
      //         fileProgressContext
      //           .filter((f) => f.commit?.id === c.id && f.folder?.isNew)
      //           .map((f) => f.folder?.dbFolder) as DbFolder[]
      //     )
      //     .flat(),
      //   'id'
      // );
      const foldersToDelete = uniqBy(
        commits
          .map((c) =>
            fileProgressContext
              .filter((f) => f.commit?.id === c.id)
              .map((f) => {
                const itemFolder = f.folder?.isNew ? [f.folder.dbFolder] : [];

                const bulkFolders =
                  ((f as any).bulkCreatedFolders || [])
                    .filter((folder) => folder.isNew)
                    .map((folder) => folder.dbFolder) || [];

                return [...itemFolder, ...bulkFolders];
              })
              .flat()
          )
          .flat(),
        'id'
      ).sort((a, b) => Number(b.id) - Number(a.id));

      // delete failed commits
      await Promise.all(
        commits.map((commit) =>
          deleteCommit({
            projectId: String(projectId),
            commit,
          })
        )
      ).catch(() => null);

      // delete created folders
      if (foldersToDelete.length) {
        await Promise.all(
          foldersToDelete.map((folder) =>
            deleteFolder({ folder, projectId: String(projectId) })
          )
        ).catch(() => null);
        queryCache.invalidateQueries('folders');
      }

      setModalData((prevData) => ({
        ...prevData,
        isLoading: false,
        visible: false,
      }));
      changeUploadingActive(false);
      setTimeout(() => setFileProgressContext([]), 300);
    } catch (err) {
      setModalData((prevData) => ({ ...prevData, isLoading: false }));
      message.error(intl.formatMessage({ id: 'generic-error' }));
      appLog('FileProgressPanel -> handleDeleteCommitsClick -> err', err);
    }
  };

  const sortUploads = (files: FileProgress[] | AttachmentProgress[]) => {
    return files.sort((a, b) => {
      const aInQueue = !a.shouldStartUpload && !a.isDone;
      const aUploading = a.shouldStartUpload && !a.isDone;
      const aIsDone = a.shouldStartUpload && a.isDone;
      const aError = a.error;

      const bInQueue = !b.shouldStartUpload && !b.isDone;
      const bUploading = b.shouldStartUpload && !b.isDone;
      const bIsDone = b.shouldStartUpload && b.isDone;
      const bError = b.error;

      if (
        (aInQueue && bIsDone) ||
        (aUploading && bInQueue) ||
        (aUploading && bIsDone) ||
        (aUploading && bError)
      ) {
        return -1;
      }
      if (
        (aInQueue && bUploading) ||
        (aIsDone && bUploading) ||
        (aIsDone && bInQueue) ||
        (bUploading && aError)
      ) {
        return 1;
      }
      return 0;
    });
  };

  // BULK UPLOADER SETTINGS
  const isBulkProgressMode = totalUploads > DETAILED_UPLOAD_LIMIT;
  const BULK_VISIBLE_ITEMS_LIMIT = 8;

  const failedUploads = fileProgressContext.filter((item) => item.error).length;
  const uploadingUploads = fileProgressContext.filter(
    (item) => item.shouldStartUpload && !item.isDone && !item.error
  ).length;
  const queuedUploads = fileProgressContext.filter(
    (item) => !item.shouldStartUpload && !item.isDone && !item.error
  ).length;

  const progressPercent = totalUploads
    ? Math.round((completedUploads / totalUploads) * 100)
    : 0;

  const sortedProgressItems = sortUploads([...fileProgressContext] as
    | FileProgress[]
    | AttachmentProgress[]);

  const visibleProgressItems = isBulkProgressMode
    ? sortedProgressItems
        .filter(
          (item) => item.error || (item.shouldStartUpload && !item.isDone)
        )
        .slice(0, BULK_VISIBLE_ITEMS_LIMIT)
    : sortedProgressItems;

  return (
    <Fragment>
      {isBulkProgressMode && isUploadingActive && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            pointerEvents: 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
          }}>
          <div style={{ width: '3%', height: '3%' }}>
            <FontAwesomeIcon
              icon={['far', 'lock']}
              style={{ color: 'white', width: '100%', height: '100%' }}
            />
          </div>
        </div>
      )}
      <div
        css={[
          styles.container,
          isBulkProgressMode && styles.bulkContainer,
          mobile && { bottom: 100 },
        ]}>
        <div css={styles.header}>
          <div css={styles.headerTextContainer} onClick={handleExpandClick}>
            {hasItemsWithError ? (
              <IconButton
                icon={['fas', 'exclamation-circle']}
                color="red"
                tooltip={intl.formatMessage({ id: 'file-upload-failed' })}
                backgroundOff
              />
            ) : (
              <IconButton icon={['far', 'file-plus']} backgroundOff />
            )}

            <div css={styles.headerText}>
              {intl.formatMessage(
                { id: 'upload-progress' },
                { completed: completedUploads, total: totalUploads }
              )}
            </div>
          </div>
          <div>
            <IconButton
              icon={['fas', 'chevron-down']}
              customStyle={{
                transform: `rotate(${expanded ? 0 : 180}deg)`,
                transition: 'all 0.2s ease-out',
              }}
              click={handleExpandClick}
            />

            <IconButton
              icon={['far', 'times']}
              click={handleCloseClick}
              tooltip={intl.formatMessage({ id: 'close' })}
              disabled={hasItemsInProgress}
            />
          </div>
        </div>

        {isBulkProgressMode && expanded && (
          <div css={styles.bulkSummary}>
            <div css={styles.bulkSummaryTop}>
              <div>
                <div css={styles.bulkTitle}>
                  {intl.formatMessage({ id: 'bulk-upload-mode' })}
                </div>
                <div css={styles.bulkSubtitle}>
                  {intl.formatMessage(
                    { id: 'upload-progress' },
                    { completed: completedUploads, total: totalUploads }
                  )}
                </div>
              </div>

              <div css={styles.bulkPercent}>{progressPercent}%</div>
            </div>

            <Progress
              percent={progressPercent}
              showInfo={false}
              strokeWidth={7}
              status={hasItemsWithError ? 'exception' : 'active'}
            />

            <div css={styles.bulkStats}>
              <div css={styles.bulkStat}>
                <div css={styles.bulkStatValue}>{queuedUploads}</div>
                <div css={styles.bulkStatLabel}>
                  {intl.formatMessage({ id: 'queued' })}
                </div>
              </div>

              <div css={styles.bulkStat}>
                <div css={styles.bulkStatValue}>{uploadingUploads}</div>
                <div css={styles.bulkStatLabel}>
                  {intl.formatMessage({ id: 'uploading' })}
                </div>
              </div>

              <div css={styles.bulkStat}>
                <div css={styles.bulkStatValue}>{completedUploads}</div>
                <div css={styles.bulkStatLabel}>
                  {intl.formatMessage({ id: 'completed' })}
                </div>
              </div>

              <div
                css={[styles.bulkStat, failedUploads && styles.bulkStatError]}>
                <div css={styles.bulkStatValue}>{failedUploads}</div>
                <div css={styles.bulkStatLabel}>
                  {intl.formatMessage({ id: 'failed' })}
                </div>
              </div>
            </div>
          </div>
        )}

        <List
          css={[
            styles.list,
            {
              display: expanded ? 'block' : 'none',
              maxHeight: expanded ? 230 : 0,
            },
          ]}
          dataSource={visibleProgressItems}
          renderItem={(item, i) => (
            <FileProgressItem
              key={item.id}
              item={item}
              fileProgressContext={fileProgressContext}
              setFileProgressContext={setFileProgressContext}
              onFileDone={handleFileDone}
              onAttachmentDone={handleAttachmentDone}
              shouldStartUpload={item.shouldStartUpload}
            />
          )}
        />
      </div>
      <Modal
        title={
          <Typography.Text css={{ verticalAlign: '-webkit-baseline-middle' }}>
            <IconButton
              icon={['fas', 'exclamation-circle']}
              color="red"
              backgroundOff
            />
            {intl.formatMessage({ id: 'you-sure' })}
          </Typography.Text>
        }
        centered
        width={450}
        okText={intl.formatMessage({ id: 'delete' })}
        cancelText={intl.formatMessage({ id: 'cancel' })}
        visible={modalData.visible}
        okButtonProps={{
          loading: modalData.isLoading,
          danger: true,
        }}
        onOk={handleDeleteCommitsClick}
        onCancel={() =>
          setModalData((prevData) => ({
            ...prevData,
            commits: [],
            visible: false,
          }))
        }>
        <Typography.Text>
          {intl.formatMessage({ id: 'delete-commits-p1' })}:
        </Typography.Text>

        <List
          bordered
          css={{ margin: '14px 0' }}
          dataSource={modalData.commits}
          renderItem={(item) => (
            <List.Item
              extra={
                item.is_important
                  ? [
                      <IconButton
                        icon={['far', 'star']}
                        containerStyle={{ marginRight: -10 }}
                        backgroundOff
                        tooltip={intl.formatMessage({
                          id: 'important-tooltip',
                        })}
                      />,
                    ]
                  : undefined
              }>
              <Typography.Text strong>
                {parseMessagePreview(item.description, 100)}
              </Typography.Text>
            </List.Item>
          )}
        />

        <Typography.Text>
          {intl.formatMessage({ id: 'delete-commits-p2' })}
        </Typography.Text>
      </Modal>
    </Fragment>
  );
};

type ModalDataState = {
  visible: boolean;
  commits: DbCommit[];
  isLoading: boolean;
};

const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    position: 'fixed',
    bottom: 20,
    right: 20,
    backgroundColor: theme.colors.lightGreen,
    padding: '0 13px',
    width: 300,
    borderRadius: 6,
    boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.21)',
    animation: `${fadeInSideKeyframes} 0.3s ease-out`,
    zIndex: 1100,
  },
  bulkContainer: {
    width: 360,
    borderRadius: 10,
    boxShadow: '0 8px 28px 0 rgba(0, 0, 0, 0.24)',
    overflow: 'hidden',
  },
  header: {
    padding: '14px 0',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerTextContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  headerText: {
    fontWeight: 'bold',
  },
  bulkSummary: {
    overflow: 'hidden',
    paddingBottom: 10,
  },
  bulkSummaryTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  bulkTitle: {
    fontSize: 13,
    fontWeight: 700,
  },
  bulkSubtitle: {
    marginTop: 2,
    fontSize: 12,
    opacity: 0.75,
  },
  bulkPercent: {
    minWidth: 46,
    height: 28,
    padding: '0 8px',
    borderRadius: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 700,
  },
  bulkStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 7,
    marginTop: 10,
  },
  bulkStat: {
    minWidth: 0,
    padding: '7px 4px',
    borderRadius: 7,
    backgroundColor: 'rgba(255, 255, 255, 0.55)',
    textAlign: 'center',
  },
  bulkStatError: {
    backgroundColor: 'rgba(255, 220, 220, 0.8)',
  },
  bulkStatValue: {
    fontSize: 14,
    lineHeight: '17px',
    fontWeight: 700,
  },
  bulkStatLabel: {
    marginTop: 2,
    fontSize: 10,
    lineHeight: '12px',
    opacity: 0.75,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  list: {
    overflowY: 'auto',
    transition: 'all 0.2s ease-out',
  },
});

const fadeInSideKeyframes = keyframes({
  '0%': {
    opacity: 0,
    transform: ['translateX(100px)'],
  },
  '100%': {
    opacity: 1,
    transform: ['translateX(0px)'],
  },
});
